import { createContext, useContext } from 'react'
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiArrowDownSLine, RiTaskLine, RiTimeLine } from '@remixicon/react'
import { useAuth } from '../../contexts/AuthContext'
import AnnouncementBar from '../announcements/AnnouncementBar'
import AppVersion from '../AppVersion'
import NotificationBell from '../NotificationBell'
import { BugReportDrawer } from '../feedback'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '../ui/DropdownMenu'
import AccountMenu from './AccountMenu'
import { IDENTITY_BADGE, monogram } from '../../lib/identity'
import { useMyWorkspaces, type MemberWorkspace } from '../../hooks/useMyWorkspaces'
import { cx, focusRing } from '../../lib/utils'
import Logo from '../ui/Logo'

interface MemberWorkspacesValue {
  workspaces: MemberWorkspace[]
  loading: boolean
  refresh: () => Promise<void>
}

const MemberWorkspacesContext = createContext<MemberWorkspacesValue | undefined>(undefined)

/** The member's workspaces, fetched once by the layout and shared with its pages. */
export function useMemberWorkspaces(): MemberWorkspacesValue {
  const context = useContext(MemberWorkspacesContext)
  if (!context) {
    throw new Error('useMemberWorkspaces must be used within MemberLayout')
  }
  return context
}

/** Layout route for the workspace side. */
export default function MemberLayout() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const { workspaces, loading, refresh } = useMyWorkspaces()

  const current = workspaces.find((workspace) => workspace.id === workspaceId)
  const canSwitch = workspaces.length > 1

  return (
    <MemberWorkspacesContext.Provider value={{ workspaces, loading, refresh }}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <AnnouncementBar />
        <header className="sticky top-0 z-40 border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950/80">
          <div className="mx-auto flex h-16 w-full max-w-5xl items-center justify-between gap-4 px-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <Link to="/w" className="flex shrink-0 items-center gap-2.5">
                <Logo />
              </Link>

              {current && (
                <>
                  <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">
                    /
                  </span>
                  {canSwitch ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={cx(
                            'flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-gray-900 transition hover:bg-gray-100 dark:text-gray-50 dark:hover:bg-gray-900',
                            focusRing,
                          )}
                        >
                          <span
                            aria-hidden="true"
                            className={cx(
                              'flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-bold ring-1',
                              IDENTITY_BADGE,
                            )}
                          >
                            {monogram(current.name)}
                          </span>
                          <span className="truncate">{current.name}</span>
                          <RiArrowDownSLine
                            className="size-4 shrink-0 text-gray-400"
                            aria-hidden="true"
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-60">
                        <DropdownMenuLabel>{t('workspace.switch')}</DropdownMenuLabel>
                        {workspaces.map((workspace) => (
                          <DropdownMenuItem
                            key={workspace.id}
                            onClick={() => navigate(`/w/${workspace.id}`)}
                          >
                            {workspace.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="flex min-w-0 items-center gap-1.5 px-2 py-1">
                      <span
                        aria-hidden="true"
                        className={cx(
                          'flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-bold ring-1',
                          IDENTITY_BADGE,
                        )}
                      >
                        {monogram(current.name)}
                      </span>
                      <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                        {current.name}
                      </span>
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <NotificationBell />
              <AccountMenu
                trigger={
                  <button
                    aria-label={t('nav.profile')}
                    className={cx(
                      'flex size-9 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700 transition hover:bg-primary-200 dark:bg-primary-900 dark:text-primary-300 dark:hover:bg-primary-800',
                      focusRing,
                    )}
                  >
                    {user?.name?.charAt(0).toUpperCase()}
                  </button>
                }
              />
            </div>
          </div>
        </header>

        {/* The workspace's own sections. Without these the tasks page was a
            route nothing linked to: the only way in was a timeline entry,
            which scrolls away. Shown only inside a workspace, since at /w
            there is nothing yet to navigate. */}
        {current && (
          <nav
            aria-label={t('nav.sections')}
            className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="mx-auto flex w-full max-w-5xl gap-5 overflow-x-auto px-4 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <MemberTab to={`/w/${current.id}`} end icon={RiTimeLine}>
                {t('timeline.title')}
              </MemberTab>
              <MemberTab to={`/w/${current.id}/tasks`} icon={RiTaskLine}>
                {t('tasks.title')}
              </MemberTab>
            </div>
          </nav>
        )}

        {/* pb-24: see OperatorLayout — the floating bug-report button would
            otherwise cover whatever ends the page. */}
        <main className="mx-auto w-full max-w-5xl px-4 pb-24 pt-8 sm:px-6">
          <Outlet />
          <AppVersion className="mt-12 text-center" />
        </main>

        <BugReportDrawer />
      </div>
    </MemberWorkspacesContext.Provider>
  )
}

interface MemberTabProps {
  to: string
  end?: boolean
  icon: React.ElementType
  children: React.ReactNode
}

function MemberTab({ to, end, icon: Icon, children }: MemberTabProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cx(
          'relative flex shrink-0 items-center gap-2 whitespace-nowrap py-3 text-sm transition-colors',
          isActive
            ? 'font-semibold text-gray-950 dark:text-white'
            : 'font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200',
          focusRing,
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="size-4" aria-hidden="true" />
          {children}
          {isActive && (
            <span className="absolute -bottom-px left-0 right-0 h-0.5 rounded-full bg-primary-600 dark:bg-primary-500" />
          )}
        </>
      )}
    </NavLink>
  )
}
